import React, { ReactNode, useEffect, useMemo, useRef } from 'react';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from 'react-virtualized';
import {
    SearchableConvo,
    selectAppSettingByKey,
    selectFiltersByPage,
    selectSearchConvos,
    selectSearchNoChatUsers,
} from '../../../redux/slices';
import { useTypedDispatch, useTypedSelector } from '../../../redux/hooks';
import AriaListFocusContainer from '../../AriaComponents/AriaListFocusContainer';
import ContactNamesProvider from '../../../pages/phone/GetContactNamesContext';
import { setActiveConversation } from '../../../redux/slices/chatNSlice';
import { ConversationRow, ConversationThreadRow } from './Conversation/ConversationRow';
import ChatListSkeleton from '../../Skeletons/ChatListSkeleton';
import EntityIcon from '../../icons/EntityIcon';
import { useChatsLoading } from './ChatSidebar';
import { nebulaChatApi } from '../../../redux/services/nebulaChat';
import MessageSearchRow from './MessageSearchRow';

const ChatListN: React.FC<{
    searchQueryDebounced: string,
    is_threads: boolean,
}> = ({ searchQueryDebounced, is_threads }) => {
    const width = useTypedSelector(state => selectAppSettingByKey(state, 'asideWidth'));
    const filters = useTypedSelector(state => selectFiltersByPage(state, 'chatList'));

    const { chatsLoading, threadsLoading } = useChatsLoading();

    const displayChats: SearchableConvo[] = useTypedSelector(state => {
        const base = selectSearchConvos(state, searchQueryDebounced, filters, is_threads);

        if (base.length > 0) {
            return base;
        }

        return [{
            convo: {
                uuid: "__placeholder__"
            }
        } as SearchableConvo]
    });

    const dispatch = useTypedDispatch();

    const noChatUsers = useTypedSelector(state => selectSearchNoChatUsers(state, searchQueryDebounced));

    const { data: searchResult, isFetching: searchFetching } = nebulaChatApi.endpoints.searchMessagesN.useQuery({ pattern: searchQueryDebounced, limit: 25 }, { skip: !searchQueryDebounced });

    const cache = useMemo(() => new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: is_threads ? 136 : 68
    }), [is_threads]);

    const listRef = useRef<List>(null);

    const showExtended = !!searchQueryDebounced;
    const contactsRowCount = showExtended
        ? 2
        + Math.max(noChatUsers.length, 1) // divider + users (or empty placeholder)
        + Math.max((searchResult?.length || 0), 1) // divider + users (or empty placeholder)

        : 0;
    const totalRowCount = displayChats.length + 1 + contactsRowCount;

    const searchResultKey = searchResult?.map(m => m.uuid).join(',') ?? '';

    useEffect(() => {
        cache.clearAll();
        listRef.current?.recomputeRowHeights();
    }, [searchQueryDebounced, totalRowCount, searchResultKey]);

    const rowRenderer = ({ key, index, style, parent }) => {
        let display: ReactNode = null;

        if (index === 0) {
            display = (
                <div style={style}>
                    <div className='list-divider'>
                        <p>CHATS</p>
                    </div>
                </div>
            );
        } else if (index <= displayChats.length) {
            const item = displayChats[index - 1];
            if (!item) return null;

            if (index === 1 && item.convo.uuid === "__placeholder__") {
                display = (<div style={style}>
                    <div className='empty-placeholder'>
                        <p>No results match your search</p>
                    </div>
                </div>);
            } else if (item.convo.type === 'thread') {
                display = (
                    <div style={style}>
                        <ConversationThreadRow thread={item.convo} />
                    </div>
                );
            } else {
                display = (
                    <div style={style}>
                        <ConversationRow convo={item.convo} />
                    </div>
                );
            }
        } else if (showExtended) {
            const contactsDividerIndex = displayChats.length + 1;
            const contactsCount = Math.max(noChatUsers.length, 1);
            const messagesDividerIndex = contactsDividerIndex + contactsCount + 1;

            if (index === contactsDividerIndex) {
                display = (
                    <div style={style}>
                        <div className='list-divider'>
                            <p>CONTACTS</p>
                        </div>
                    </div>
                );
            } else if (index < messagesDividerIndex) {
                const userIndex = index - contactsDividerIndex - 1;

                if (noChatUsers.length === 0) {
                    display = (
                        <div style={style}>
                            <div className='empty-placeholder'>
                                <p>No contacts found</p>
                            </div>
                        </div>
                    );
                } else {
                    const user = noChatUsers[userIndex];
                    if (user) {
                        display = (
                            <div style={style}>
                                <div
                                    className='chat__chat-row'
                                    onClick={() => dispatch(setActiveConversation(user.uuid))}
                                    onKeyDown={event => {
                                        if (event.key === 'Enter') {
                                            event.preventDefault();
                                            dispatch(setActiveConversation(user.uuid));
                                        }
                                    }}
                                    /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */
                                    tabIndex={0}
                                >
                                    <div className='recent-row'>
                                        <EntityIcon
                                            key={user.uuid}
                                            type='user'
                                            details={{
                                                sip_name: user.name,
                                                display_name: user.nickname,
                                                avatar_key: user.avatar_key,
                                                blf: true,
                                                uuid: user.uuid
                                            }}
                                        />
                                        <div className='recent-row__content'>
                                            <div>
                                                <h3>{user.nickname}</h3>
                                            </div>
                                            <div className='recent-row__message-preview'>
                                                <p>Start New Chat</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    }
                }
            } else if (index === messagesDividerIndex) {
                display = (
                    <div style={style}>
                        <div className='list-divider'>
                            <p>MESSAGES</p>
                        </div>
                    </div>
                );
            } else {
                const messageIndex = index - messagesDividerIndex - 1;
                const messages = searchResult || [];

                if (messages.length === 0) {
                    display = (
                        <div style={style}>
                            <div className='empty-placeholder'>
                                <p>{searchFetching ? 'Loading...' : 'No messages found'}</p>
                            </div>
                        </div>
                    );
                } else {
                    const message = messages[messageIndex];
                    if (message) {
                        display = (
                            <div style={style}>
                                <MessageSearchRow
                                    message={message}
                                    searchQuery={searchQueryDebounced}
                                />
                            </div>
                        );
                    }
                }
            }
        }

        if (!display) return null;

        return (
            <CellMeasurer
                key={key}
                cache={cache}
                parent={parent}
                columnIndex={0}
                rowIndex={index}
            >
                {display}
            </CellMeasurer>
        );
    };

    const isLoading = (chatsLoading && !is_threads) || (threadsLoading && is_threads);

    return (
        <ContactNamesProvider>
            <AriaListFocusContainer
                className='recent__chat-list list-container'
                title='Chat List'
            >
                {isLoading ? (
                    <ChatListSkeleton />
                ) : (
                    <AutoSizer>
                        {({ height }) => (
                            <List
                                ref={listRef}
                                width={width}
                                height={height}
                                // rowCount={dataSet.length + displayChats.length}
                                rowCount={totalRowCount}
                                rowRenderer={rowRenderer}
                                overscanRowCount={3}
                                rowHeight={cache.rowHeight}
                                deferredMeasurementCache={cache}
                                className='windowed-chat-list-container'
                                tabIndex={-1}
                            />
                        )}
                    </AutoSizer>
                )}
            </AriaListFocusContainer>
        </ContactNamesProvider>
    );
}


export default ChatListN;
